import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Copyright, Globe, Award, ShieldAlert, FileCheck2 } from "lucide-react";
import { StarSpinner } from "@/components/StarSpinner";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-oxblood/10 text-oxblood" },
  registered: { label: "Registered", cls: "bg-hunter/10 text-hunter" },
  renewal_due: { label: "Renewal Due", cls: "bg-brass/20 text-brass2" },
  abandoned: { label: "Abandoned", cls: "bg-slate2/15 text-slate2" },
};

const ASSET_ICONS = {
  trademark: Award,
  copyright: Copyright,
  domain: Globe,
  patent: FileCheck2,
  trade_secret: ShieldAlert,
};

const ASSET_LABEL = {
  trademark: "Trademark",
  copyright: "Copyright",
  domain: "Domain",
  patent: "Patent",
  trade_secret: "Trade Secret",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  if (Number.isNaN(then.getTime())) return null;
  const now = new Date();
  return Math.ceil((then - now) / (1000 * 60 * 60 * 24));
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function IPPortfolio() {
  const [assets, setAssets] = useState(null);

  useEffect(() => {
    // `api` is a stable module-level import; running once on mount is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/portal/ip-assets").then(({ data }) => setAssets(data)).catch((err) => {
      if (typeof console !== "undefined") console.warn("[ip-portfolio] /portal/ip-assets failed:", err);
      setAssets([]);
    });
  }, []);

  const alerts = useMemo(() => {
    if (!assets) return { dueSoon: 0, pending: 0, total: 0 };
    const dueSoon = assets.filter((a) => {
      const d = daysUntil(a.renewal_date);
      return d !== null && d <= 90 && d >= 0;
    }).length;
    const pending = assets.filter((a) => a.status === "pending").length;
    return { dueSoon, pending, total: assets.length };
  }, [assets]);

  if (assets === null) {
    return (
      <div className="mt-12" data-testid="portal-ip-loading">
        <StarSpinner label="Loading portfolio" />
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div
        className="mt-10 p-12 border border-transparent bg-linen/40 text-center"
        data-testid="portal-ip-empty"
      >
        <div className="font-serif italic text-2xl text-slate2">
          No intellectual property assets are currently under the firm&apos;s stewardship.
        </div>
        <p className="mt-4 max-w-lg mx-auto text-slate2 font-light text-sm">
          Marks, copyrights, domains, and patents that the firm files or manages on your behalf
          will appear here. Contact your counsel to add an asset to the portfolio.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="portal-ip-portfolio">
      {/* Renewal Alert Summary */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-brass/30" data-testid="portal-ip-alerts">
        <SummaryCell
          testId="ip-summary-total"
          kicker="Under Stewardship"
          value={alerts.total}
          copy={alerts.total === 1 ? "Asset actively managed" : "Assets actively managed"}
        />
        <SummaryCell
          testId="ip-summary-due"
          kicker="Requires Attention"
          value={alerts.dueSoon}
          copy={
            alerts.dueSoon === 0
              ? "No renewals due within 90 days"
              : `Renewal${alerts.dueSoon > 1 ? "s" : ""} due within 90 days`
          }
          accent={alerts.dueSoon > 0}
        />
        <SummaryCell
          testId="ip-summary-pending"
          kicker="In Prosecution"
          value={alerts.pending}
          copy={alerts.pending === 1 ? "Filing awaiting examination" : "Filings awaiting examination"}
        />
      </div>

      {/* Assets Table */}
      <div className="mt-10 bg-brass/30 grid grid-cols-1 gap-px" data-testid="portal-ip-list">
        <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
          <div className="col-span-4">Asset</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Filed</div>
          <div className="col-span-2">Renewal</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {assets.map((a) => {
          const Icon = ASSET_ICONS[a.asset_type] || Award;
          const status = STATUS_META[a.status] || STATUS_META.pending;
          const dLeft = daysUntil(a.renewal_date);
          const soon = dLeft !== null && dLeft <= 180 && dLeft >= 0;
          return (
            <div
              key={a.id}
              className="bg-ivory grid grid-cols-12 gap-4 px-6 py-6 items-start"
              data-testid={`portal-ip-${a.id}`}
            >
              <div className="col-span-4 flex items-start gap-3">
                <Icon size={20} className="text-brass shrink-0 mt-1" />
                <div>
                  <div className="font-serif text-xl text-navy leading-tight">{a.name}</div>
                  <div className="mt-1 text-xs text-slate2/80 uppercase tracking-[0.2em]">
                    {a.jurisdiction || "—"}
                    {a.classification ? ` · ${a.classification}` : ""}
                  </div>
                  {a.owner_of_record && (
                    <div className="mt-1 text-xs text-slate2 font-cormorant italic">
                      Owner of record: {a.owner_of_record}
                    </div>
                  )}
                  {(a.serial_number || a.registration_number) && (
                    <div className="mt-2 text-xs text-slate2 font-light">
                      {a.serial_number && <span>Serial {a.serial_number}</span>}
                      {a.serial_number && a.registration_number && <span> &nbsp;·&nbsp; </span>}
                      {a.registration_number && <span>Reg {a.registration_number}</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 font-serif italic text-slate2 text-sm">
                {ASSET_LABEL[a.asset_type] || a.asset_type}
              </div>
              <div className="col-span-2 text-sm text-slate2 font-light">
                {fmtDate(a.filing_date)}
              </div>
              <div className="col-span-2 text-sm">
                <div className={`font-light ${soon ? "text-oxblood" : "text-slate2"}`}>
                  {fmtDate(a.renewal_date)}
                </div>
                {soon && (
                  <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-oxblood font-medium">
                    {dLeft <= 0 ? "Overdue" : `In ${dLeft}d`}
                  </div>
                )}
              </div>
              <div className="col-span-2 text-right">
                <span
                  className={`inline-block px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-medium ${status.cls}`}
                >
                  {status.label}
                </span>
              </div>
              {a.notes && (
                <div className="col-span-12 mt-2 pt-4 hairline-top font-serif italic text-slate2 text-sm leading-relaxed">
                  &ldquo;{a.notes}&rdquo;
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCell({ kicker, value, copy, accent, testId }) {
  return (
    <div className="bg-ivory p-8" data-testid={testId}>
      <div className="kicker">{kicker}</div>
      <div
        className={`mt-3 font-serif text-6xl leading-none ${
          accent ? "text-oxblood" : "text-navy"
        }`}
      >
        {value}
      </div>
      <div className="mt-3 text-slate2 font-light text-sm">{copy}</div>
    </div>
  );
}
